import React, { useEffect, useMemo, useState } from "react";
import { birthdayService } from "../../features/organization/services/birthdayService";
import { BASE_URL } from "../../utils/api";

const quotes = [
  "Wishing you a day filled with happiness and a year filled with joy!",
  "May your birthday be full of wonderful moments and great memories.",
  "Your team is celebrating you today—have a fantastic birthday!",
  "Wishing you success, happiness and good health in the year ahead.",
];

const decorations = ["🎈", "✨", "🎁", "🎉", "🎊", "🍰"];

function avatarUrl(value) {
  if (!value) return "";
  if (String(value).startsWith("http")) return value;
  return `${BASE_URL}/${String(value).replace(/^\//, "")}`;
}

export default function BirthdayCelebration({ isBirthday, name, people = [], isSelf = false, onWish }) {
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [wishes, setWishes] = useState([]);
  const [wishIndex, setWishIndex] = useState(0);
  const celebrants = useMemo(() => people.length ? people : [{ name, isSelf }], [people, name, isSelf]);

  useEffect(() => {
    if (!isBirthday) return undefined;
    const timer = window.setInterval(() => setQuoteIndex((value) => (value + 1) % quotes.length), 5000);
    if (isSelf) birthdayService.getReceivedWishes().then(setWishes).catch(() => setWishes([]));
    return () => window.clearInterval(timer);
  }, [isBirthday, isSelf]);

  useEffect(() => {
    if (!wishes.length) return undefined;
    const timer = window.setInterval(() => setWishIndex((value) => (value + 1) % wishes.length), 4000);
    return () => window.clearInterval(timer);
  }, [wishes]);

  if (!isBirthday || !celebrants.length) return null;

  return (
    <section className="birthday-celebration relative overflow-hidden rounded-[2rem] border-2 border-amber-200 p-5 shadow-xl shadow-amber-100/60 sm:p-7" style={{ background: "linear-gradient(135deg, #fffdf5 0%, #fff7ed 50%, #fdf2f8 100%)" }}>
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {Array.from({ length: 18 }).map((_, index) => (
          <span key={index} className="birthday-float absolute bottom-[-40px] opacity-40" style={{ left: `${(index * 37) % 100}%`, animationDelay: `${(index % 8) * 0.7}s`, animationDuration: `${6 + (index % 4)}s` }}>{decorations[index % decorations.length]}</span>
        ))}
      </div>

      <div className="relative z-10 text-center">
        <p className="text-[10px] font-black uppercase tracking-[0.25em]" style={{ color: "#b45309" }}>{isSelf ? "✨ Your Special Day Is Here ✨" : "🎊 Today's Company Celebration 🎊"}</p>
        <h2 className="mt-2 text-xl font-black uppercase sm:text-2xl" style={{ color: "#172033" }}>Happy Birthday! 🎂</h2>
        <p className="mx-auto mt-2 max-w-2xl text-xs font-bold italic" style={{ color: "#475569" }}>“{quotes[quoteIndex]}”</p>

        <div className="mx-auto mt-5 flex max-w-5xl flex-wrap justify-center gap-3">
          {celebrants.map((person, index) => (
            <article key={person.id || `${person.name}-${index}`} className="flex max-w-full items-center gap-3 rounded-2xl border border-amber-200 bg-white/95 px-4 py-3 text-left shadow-lg shadow-amber-100/70">
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border-2 border-amber-300 bg-amber-50">
                {person.profile_img ? <img src={avatarUrl(person.profile_img)} alt={person.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-xl">🎉</div>}
              </div>
              <div className="min-w-0">
                <p className="truncate text-xs font-black uppercase text-slate-800">{person.name}</p>
                <p className="truncate text-[9px] font-bold uppercase tracking-wider text-amber-600">{person.department || "Our Team"} · Birthday Star</p>
              </div>
              {!person.isSelf && onWish && <button type="button" onClick={() => onWish(person.id, person.name)} className="shrink-0 rounded-xl bg-customRed px-3 py-2 text-[9px] font-black uppercase tracking-wider text-white shadow transition-all hover:brightness-90 active:scale-95">Send Wish</button>}
            </article>
          ))}
        </div>

        {isSelf && wishes.length > 0 && <div className="mx-auto mt-4 max-w-xl rounded-xl border border-pink-100 bg-white/80 px-4 py-3 text-xs text-slate-600"><span className="font-black text-customRed">{wishes[wishIndex].sender_name}:</span> “{wishes[wishIndex].message}”</div>}
      </div>

      <style>{`
        @keyframes birthday-float { 0% { transform: translateY(0) rotate(0); opacity: 0; } 10% { opacity: .9; } 100% { transform: translateY(-360px) rotate(100deg); opacity: 0; } }
        .birthday-float { animation: birthday-float infinite linear; }
        @media (prefers-reduced-motion: reduce) { .birthday-float { display: none; } .birthday-celebration .animate-pulse { animation: none; } }
      `}</style>
    </section>
  );
}
